/** @type {import('zudoku').ZudokuConfig} */
import React from "react";
import { Button } from "zudoku/ui/Button.js";
import changelogs from "./build/changelogs.json";

const config = {
  site: {
    title: "Moneybird API Documentation",
    logo: {
      src: { light: "/assets/moneybird.svg", dark: "/assets/moneybird-dark.svg" },
      alt: "Moneybird logo",
      width: "140px",
    },
    footer: {
      position: "start",
    },
  },
  metadata: {
    title: "Moneybird API Documentation",
    description: "This is the Moneybird API documentation. Here you can find all the information you need to use the Moneybird API.",
    logo: "/assets/moneybird.svg",
    favicon: "/assets/favicon.svg",
    applicationName: "Moneybird API Documentation",
    referrer: "no-referrer",
    keywords: ["moneybird", "api", "documentation", "rest", "webhooks"],
    authors: ["Moneybird"],
    creator: "Moneybird",
    publisher: "Moneybird",
  },
  navigation: [
    {
      type: "category",
      icon: "book-open",
      label: "Documentation",
      items: [
        {
          type: "doc",
          file: "/introduction",
          label: "Introduction",
          icon: "rocket",
        },
        {
          type: "category",
          label: "API Integration guides",
          icon: "cog",
          items: [
            "/integration/getting-started",
            "/integration/importing-external-invoices",
            "/integration/creating-sales-invoices",
            "/integration/sending-sales-invoices-peppol",
            "/integration/managing-subscriptions",
            "/integration/hosted-subscription-management",
          ],
        },
        {
          type: "doc",
          file: "/authentication",
          label: "Authentication",
          icon: "shield-check",
        },
        {
          type: "category",
          label: "Webhooks",
          icon: "webhook",
          items: [
            "/webhooks/getting-started",
            "/webhooks/verifying-signatures",
            "/webhooks/subscribing-to-payment-updates",
            "/webhooks/events",
            "/webhooks/payload",
          ],
        },
        {
          type: "category",
          label: "MCP & CLI",
          icon: "wrench",
          items: [
            "/tools/mcp",
            "/tools/cli",
          ],
        },
      ],
    },
    {
      type: "link",
      icon: "code",
      to: "/api",
      label: "API Reference",
    },
    {
      type: "category",
      icon: "list",
      label: "Changelog",
      items: changelogs.map((changelog) => ({
        type: "doc",
        file: changelog.path,
        label: changelog.name,
      })),
    },
  ],
  redirects: [
    { from: "/", to: "/introduction" },
    { from: "/changelog", to: changelogs[0]?.path },
    ...changelogs.map((changelog) => ({
      from: `/${changelog.name.replace(/-/g, "/")}/changelog.html`,
      to: changelog.path,
    })),
  ],
  docs: {
    files: "pages/**/*.{md,mdx}", // Your markdown files
    publishMarkdown: true, // Generate .md files
    llms: {
      llmsTxt: true, // Generate llms.txt
      llmsTxtFull: true, // Generate llms-full.txt
      includeProtected: false, // Exclude protected routes
    },
  },
  apis: {
    type: "file",
    input: "./apis/openapi.yaml",
    path: "/api",
    options: {
      disablePlayground: true,
      expandAllTags: false,
      showInfoPage: false,
    },
  },
  theme: {
    fonts: {
      sans: "Inter",
      serif: "Lora",
      mono: "JetBrains Mono",
    },
    light: {
      background: "#ffffff",
      foreground: "#2c2c2e",
      card: "#ffffff",
      cardForeground: "#2c2c2e",
      popover: "#ffffff",
      popoverForeground: "#2c2c2e",
      primary: "#0073e6",
      primaryForeground: "#ffffff",
      secondary: "#e8e8ed",
      secondaryForeground: "#2c2c2e",
      muted: "#f8f8fa",
      mutedForeground: "#636366",
      accent: "#f2f2f7",
      accentForeground: "#2c2c2e",
      destructive: "#eb0000",
      destructiveForeground: "#ffffff",
      border: "#e8e8ed",
      input: "#e8e8ed",
      ring: "#0073e6",
    },
    dark: {
      background: "#000000",
      foreground: "#ffffff",
      card: "#000000",
      cardForeground: "#ffffff",
      popover: "#1C1C1E",
      popoverForeground: "#AEAEB2",
      primary: "#2491ff",
      primaryForeground: "#ffffff",
      secondary: "#2c2c2e",
      secondaryForeground: "#ffffff",
      muted: "#242426",
      mutedForeground: "#8e8e93",
      accent: "#2c2c2e",
      accentForeground: "#ffffff",
      destructive: "#ff5757",
      destructiveForeground: "#1C1C1E",
      border: "#2c2c2e",
      input: "#2c2c2e",
      ring: "#2491ff",
    },
    customCss: {
      ":root": {
        "--color-red-400": "#ff5757",
        "--color-red-500": "#eb0000",
        "--color-red-600": "#eb0000",
        "--color-red-800": "#7a0000",
        "--color-orange-500": "#9e6900",
        "--color-amber-600": "#754e00",
        "--color-yellow-100": "#fff6e5",
        "--color-yellow-200": "#ffdf9e",
        "--color-yellow-300": "#ffc34d",
        "--color-yellow-400": "#c28100",
        "--color-yellow-500": "#9e6900",
        "--color-yellow-600": "#754e00",
        "--color-yellow-700": "#523600",
        "--color-yellow-800": "#523600",
        "--color-green-50": "#f0f9f9",
        "--color-green-200": "#c1ebe1",
        "--color-green-300": "#94dbce",
        "--color-green-400": "#36a18d",
        "--color-green-500": "#2b8272",
        "--color-green-600": "#2b8272",
        "--color-green-700": "#15423b",
        "--color-green-800": "#15423b",
        "--color-green-950": "#15423b",
        "--color-emerald-400": "#36a18d",
        "--color-emerald-600": "#216358",
        "--color-sky-400": "var(--primary)",
        "--color-sky-600": "var(--primary)",
        "--color-sky-800": "#003b75",
        "--color-blue-50": "#f5fbff",
        "--color-blue-100": "#e5f5ff",
        "--color-blue-200": "#cce6ff",
        "--color-blue-400": "#2491ff",
        "--color-blue-500": "#0073e6",
        "--color-blue-600": "#0057ad",
        "--color-blue-700": "#003b75",
        "--color-blue-900": "#003b75",
        "--color-blue-950": "#003b75",
        "--color-indigo-400": "#9189c8",
        "--color-indigo-600": "#5a4ca2",
        "--color-purple-300": "#cfcce0",
        "--color-purple-400": "#9189c8",
        "--color-purple-500": "#786ab9",
        "--color-purple-600": "#786ab9",
        "--color-purple-700": "#3e336c",
        "--color-rose-50": "#ffebeb",
        "--color-rose-100": "#ffebeb",
        "--color-rose-300": "#ffbdbd",
      },
      ".dark": {
        "--color-green-600": "#36a18d",
        "--color-red-600": "#ff5757",
        "--color-sky-600": "var(--primary)",
        "--color-purple-600": "#9189c8",
        "--color-blue-500": "#2491ff",
      },
      "[data-linked-param]": {
        "--param-color": "var(--secondary-foreground) !important",
        "--border-color": "var(--secondary) !important",
        "--background-color": "var(--secondary) !important",
        "padding": "4px 6px",
        "border-radius": "8px",
      },
      "[data-linked-param][data-active='true']": {
        "border-color": "var(--secondary) !important",
        "background-color": "var(--background) !important",
        "bottom": "0px",
        "box-shadow": "0 0 4px var(--secondary) !important",
        "border-radius": "8px",
      },
      "aside [data-linked-param]": {
        "margin": "4px",
      },
      "h1": {
        "font-family": "Outfit, system-ui, sans-serif !important",
        "font-weight": "600",
      },
      ".leading-6": {
        "line-height": "calc(var(--spacing) * 8)",
      },
      "footer .pt-20": {
        "padding-top": "2rem",
      },
      "@media (width < 1024px)": {
        "footer .items-center": {
          "display": "flex",
          "flex-direction": "column-reverse",
          "gap": "2rem",
        },
      },
      "@media (width > 1280px)": {
        ":root": {
          "--side-nav-width": "calc(var(--spacing) * 100)",
        },
      },
      "nav" : {
        "transition": "all .2s ease-in-out",
      },
      "div.border-t:has(> a[href='https://zudoku.dev'])": {
        "display": "none",
      },
      "a[href='https://zudoku.dev']": {
        "display": "none",
      },
      ".header-buttons-section-right:after": {
        "content": "''",
        "display": "block",
        "width": "1px",
        "height": "24px",
        "background-color": "var(--border)",
        "margin": "0 8px",
      },
      ".dark header > div > button": {
        "color": "var(--background)",
      },
      "code.inline": {
        "overflow-wrap": "normal",
      },
    },
  },
  slots: {
    "head-navigation-end": () => (
      <div className="flex items-center">
        <div className="flex items-center header-buttons-section-right">
          <Button variant="ghost" size="icon" asChild>
            <a href="https://github.com/moneybird" target="_blank" rel="noopener noreferrer" title="Github Moneybird" aria-label="Github Moneybird">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor" className="w-6 h-6" alt="Github logo">
                <path d="M237.9 461.4C237.9 463.4 235.6 465 232.7 465C229.4 465.3 227.1 463.7 227.1 461.4C227.1 459.4 229.4 457.8 232.3 457.8C235.3 457.5 237.9 459.1 237.9 461.4zM206.8 456.9C206.1 458.9 208.1 461.2 211.1 461.8C213.7 462.8 216.7 461.8 217.3 459.8C217.9 457.8 216 455.5 213 454.6C210.4 453.9 207.5 454.9 206.8 456.9zM251 455.2C248.1 455.9 246.1 457.8 246.4 460.1C246.7 462.1 249.3 463.4 252.3 462.7C255.2 462 257.2 460.1 256.9 458.1C256.6 456.2 253.9 454.9 251 455.2zM316.8 72C178.1 72 72 177.3 72 316C72 426.9 141.8 521.8 241.5 555.2C254.3 557.5 258.8 549.6 258.8 543.1C258.8 536.9 258.5 502.7 258.5 481.7C258.5 481.7 188.5 496.7 173.8 451.9C173.8 451.9 162.4 422.8 146 415.3C146 415.3 123.1 399.6 147.6 399.9C147.6 399.9 172.5 401.9 186.2 425.7C208.1 464.3 244.8 453.2 259.1 446.6C261.4 430.6 267.9 419.5 275.1 412.9C219.2 406.7 162.8 398.6 162.8 302.4C162.8 274.9 170.4 261.1 186.4 243.5C183.8 237 175.3 210.2 189 175.6C209.9 169.1 258 202.6 258 202.6C278 197 299.5 194.1 320.8 194.1C342.1 194.1 363.6 197 383.6 202.6C383.6 202.6 431.7 169 452.6 175.6C466.3 210.3 457.8 237 455.2 243.5C471.2 261.2 481 275 481 302.4C481 398.9 422.1 406.6 366.2 412.9C375.4 420.8 383.2 435.8 383.2 459.3C383.2 493 382.9 534.7 382.9 542.9C382.9 549.4 387.5 557.3 400.2 555C500.2 521.8 568 426.9 568 316C568 177.3 455.5 72 316.8 72zM169.2 416.9C167.9 417.9 168.2 420.2 169.9 422.1C171.5 423.7 173.8 424.4 175.1 423.1C176.4 422.1 176.1 419.8 174.4 417.9C172.8 416.3 170.5 415.6 169.2 416.9zM158.4 408.8C157.7 410.1 158.7 411.7 160.7 412.7C162.3 413.7 164.3 413.4 165 412C165.7 410.7 164.7 409.1 162.7 408.1C160.7 407.5 159.1 407.8 158.4 408.8zM190.8 444.4C189.2 445.7 189.8 448.7 192.1 450.6C194.4 452.9 197.3 453.2 198.6 451.6C199.9 450.3 199.3 447.3 197.3 445.4C195.1 443.1 192.1 442.8 190.8 444.4zM179.4 429.7C177.8 430.7 177.8 433.3 179.4 435.6C181 437.9 183.7 438.9 185 437.9C186.6 436.6 186.6 434 185 431.7C183.6 429.4 181 428.4 179.4 429.7z"/>
              </svg>
            </a>
          </Button>
        </div>
        <div className="flex items-center header-buttons-section-right">
          <Button variant="ghost" size="icon" asChild>
            <a href="https://raw.githubusercontent.com/moneybird/openapi/refs/heads/main/openapi.yml" target="_blank" rel="noopener noreferrer" title="Download OpenAPI Specification" aria-label="Download OpenAPI Specification">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor" className="w-5 h-5" alt="Download OpenAPI Specification">
                <path d="M488 576C501.3 576 512 565.3 512 552C512 538.7 501.3 528 488 528L152 528C138.7 528 128 538.7 128 552C128 565.3 138.7 576 152 576L488 576zM303 441C312.4 450.4 327.6 450.4 336.9 441L473 305C482.4 295.6 482.4 280.4 473 271.1C463.6 261.8 448.4 261.7 439.1 271.1L344.1 366.1L344.1 88C344.1 74.7 333.4 64 320.1 64C306.8 64 296.1 74.7 296.1 88L296.1 366.1L201.1 271.1C191.7 261.7 176.5 261.7 167.2 271.1C157.9 280.5 157.8 295.7 167.2 305L303 441z"/>
              </svg>
            </a>
          </Button>
          <Button variant="ghost" size="icon" asChild>
            <a href="/feed.xml" download="feed.xml" title="Download RSS feed" aria-label="Download RSS feed">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor" className="w-5 h-5" alt="RSS feed">
              <path d="M120 280C252.5 280 360 387.5 360 520C360 533.2 349.3 544 336 544C322.8 544 312 533.2 312 520C312 414 226 328 120 328C106.8 328 96 317.2 96 304C96 290.8 106.8 280 120 280zM120 96C354.2 96 544 285.8 544 520C544 533.2 533.3 544 520 544C506.8 544 496 533.2 496 520C496 312.3 327.7 144 120 144C106.8 144 96 133.2 96 120C96 106.8 106.8 96 120 96zM178 529.9C159.2 548.7 128.9 548.7 110.1 529.9C91.3 511.1 91.3 480.8 110.1 462C128.9 443.2 159.2 443.2 178 462C196.8 480.8 196.8 511.1 178 529.9z"/>
              </svg>
            </a>
          </Button>
        </div>
      </div>
    ),
    "footer-before": () => (
      <div>
        <h3 className="text-sm font-semibold">Need help?</h3>
        <p className="text-sm text-muted-foreground">Questions about the Moneybird API? We are more than willing to help! E-mail us at <a href="mailto:support@moneybird.com" className="text-foreground underline underline-offset-4 hover:underline-offset-2 transition-all duration-300">support@moneybird.com</a> (support available in Dutch and English).</p>
        <img src="/assets/moneybird-grey.svg" alt="Moneybird logo" className="w-35 mt-8" />
      </div>
    ),
  },
  // Disable search in development, use yarn build && serve dist to test search
  search: import.meta.env.DEV ? undefined : {
    type: "pagefind",
    maxSubResults: 3,
    ranking: {
      termFrequency: 0.8,
      pageLength: 0.6,
      termSimilarity: 1.2,
      termSaturation: 1.2,
    },
  },
};

export default config;
